import { useMemo, useState } from "react";
import { AlertTriangle, Camera, KeyRound, Lock, Mic, Shield, ShieldCheck, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AionEmptyState } from "@/components/aion/AionEmptyState";
import { AionSectionCard } from "@/components/aion/AionSectionCard";
import {
  createTrustMemoryGrant,
  revokeOtherTrustAuthSessions,
  revokeTrustAuthSession,
  revokeTrustClientSession,
  revokeTrustMemoryGrant,
  setTrustAionStop,
  updateTrustAutonomy,
  updateTrustSettings,
} from "@/services/api";

const scopeOptions = [
  { id: "MEMORY_RECALL", label: "Erinnerungen" },
  { id: "MEMORY_TOPICS", label: "Themen" },
  { id: "STRUCTURED_MEMORY", label: "Notizen" },
  { id: "PERSONAL_CONTEXT", label: "Persönlicher Kontext" },
];

const statusLabel = (enabled, onLabel = "AN", offLabel = "AUS") => (enabled ? onLabel : offLabel);

export const TrustCenterPanel = ({ trustCenter, trustCenterStatus, trustCenterError, onRetryTrustCenter, token, onRefresh, isAdmin }) => {
  const [busyKey, setBusyKey] = useState("");
  const [error, setError] = useState("");
  const [shareTarget, setShareTarget] = useState("");
  const [shareReason, setShareReason] = useState("Für Alltagshilfe freigeben");
  const [selectedScopes, setSelectedScopes] = useState(["MEMORY_RECALL"]);
  const activeTrustCenter = trustCenter;

  const recipients = activeTrustCenter?.memory?.available_recipients || [];

  const sessionSummary = useMemo(() => ({
    auth: activeTrustCenter?.remote_access?.auth_sessions || [],
    clients: activeTrustCenter?.remote_access?.client_sessions || [],
  }), [activeTrustCenter]);

  const runAction = async (key, action) => {
    setBusyKey(key);
    setError("");
    try {
      await action();
      if (onRetryTrustCenter) {
        await onRetryTrustCenter();
      } else {
        await onRefresh?.();
      }
    } catch (actionError) {
      setError(String(actionError?.message || "Aktion fehlgeschlagen."));
    } finally {
      setBusyKey("");
    }
  };

  const toggleScope = (scopeId) => {
    setSelectedScopes((prev) => (prev.includes(scopeId) ? prev.filter((item) => item !== scopeId) : [...prev, scopeId]));
  };

  if (trustCenterStatus === "ERROR" && !activeTrustCenter) {
    return (
      <div className="aion-view aion-view-stack" data-testid="trust-center-panel">
        <AionEmptyState title="Trust Center konnte nicht geladen werden." description={trustCenterError || error || "Bitte versuche es erneut."} testId="trust-center-error-state" />
        <Button onClick={() => void onRetryTrustCenter?.()} data-testid="trust-center-retry-button">Erneut laden</Button>
      </div>
    );
  }

  if (trustCenterStatus === "EMPTY" && !activeTrustCenter) {
    return (
      <div className="aion-view aion-view-stack" data-testid="trust-center-panel">
        <AionEmptyState title="Kein Trust Center Datensatz gefunden." description="Sobald Sicherheitsdaten vorhanden sind, erscheinen sie hier." testId="trust-center-empty-state" />
        <Button onClick={() => void onRetryTrustCenter?.()} data-testid="trust-center-retry-button">Neu laden</Button>
      </div>
    );
  }

  if (!activeTrustCenter) {
    return (
      <div className="aion-view aion-view-stack" data-testid="trust-center-panel">
        <AionEmptyState title="Trust Center lädt noch." description="Die echten Sicherheits- und Privatsphäre-Daten werden gerade geladen." testId="trust-center-loading-state" />
      </div>
    );
  }

  const aionStopNextEnabled = !activeTrustCenter.autonomy.aion_stop?.enabled;

  return (
    <div className="aion-view aion-view-stack" data-testid="trust-center-panel">
      {error ? <div className="aion-error-box" data-testid="trust-center-error-box">{error}</div> : null}

      <AionSectionCard title="Sofortschutz" eyebrow="TRUST CENTER" subtitle="Mikrofon und Kamera greifen direkt in die vorhandene Laufzeit ein." testId="trust-center-privacy-card">
        <div className="aion-list" data-testid="trust-center-privacy-list">
          <div className="aion-list-row" data-testid="trust-center-microphone-row">
            <div><strong><Mic className="aion-list-icon" aria-hidden="true" /> Mikrofon</strong><small>{activeTrustCenter.privacy.microphone.privacy.notice}</small></div>
            <Button
              variant={activeTrustCenter.privacy.microphone.enabled ? "outline" : "default"}
              onClick={() => runAction("mic", () => updateTrustSettings({ microphone_enabled: !activeTrustCenter.privacy.microphone.enabled }, token))}
              data-testid="trust-center-microphone-toggle-button"
            >
              {busyKey === "mic" ? "Speichert …" : statusLabel(activeTrustCenter.privacy.microphone.enabled, "Ausschalten", "Einschalten")}
            </Button>
          </div>
          <div className="aion-list-row" data-testid="trust-center-camera-row">
            <div><strong><Camera className="aion-list-icon" aria-hidden="true" /> Kamera</strong><small>{activeTrustCenter.privacy.camera.notice}</small></div>
            <Button
              variant={activeTrustCenter.privacy.camera.enabled ? "outline" : "default"}
              onClick={() => runAction("camera", () => updateTrustSettings({ camera_enabled: !activeTrustCenter.privacy.camera.enabled }, token))}
              data-testid="trust-center-camera-toggle-button"
            >
              {busyKey === "camera" ? "Speichert …" : statusLabel(activeTrustCenter.privacy.camera.enabled, "Ausschalten", "Einschalten")}
            </Button>
          </div>
        </div>
      </AionSectionCard>

      <AionSectionCard title="Autonomie & Safety" eyebrow="AION" subtitle="Die vorhandene Governor- und Permission-Logik jetzt verständlich gesteuert." testId="trust-center-autonomy-card">
        <div className="aion-section-nav" data-testid="trust-center-autonomy-levels">
          {(activeTrustCenter.autonomy.friendly_levels || []).map((level) => (
            <button
              type="button"
              key={level.level}
              className={`aion-section-nav-item${activeTrustCenter.autonomy.profile?.autonomy_level === level.level ? " aion-section-nav-item-active" : ""}`}
              onClick={() => runAction(`autonomy-${level.level}`, () => updateTrustAutonomy({ autonomy_level: level.level }, token))}
              data-testid={`trust-center-autonomy-level-${level.level}-button`}
            >
              {level.level} · {level.label}
            </button>
          ))}
        </div>
        <div className="aion-note" data-testid="trust-center-autonomy-summary">
          Safe Mode: {activeTrustCenter.autonomy.safe_mode_active ? "aktiv" : activeTrustCenter.autonomy.governor_mode}
          {" · "}AION STOP: {activeTrustCenter.autonomy.aion_stop?.enabled ? "aktiv" : "aus"}
        </div>
        {isAdmin ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant={activeTrustCenter.autonomy.aion_stop?.enabled ? "default" : "outline"}
                data-testid="trust-center-aion-stop-button"
              >
                {busyKey === "aion-stop" ? "Aktualisiert …" : activeTrustCenter.autonomy.aion_stop?.enabled ? "AION STOP freigeben" : "AION STOP aktivieren"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent
              className="aion-confirm-dialog overflow-hidden border-white/70 bg-background/95 shadow-[0_36px_90px_rgba(28,25,23,0.22)] backdrop-blur-xl sm:rounded-[28px]"
              data-testid="trust-center-aion-stop-dialog"
            >
              <AlertDialogHeader className="relative z-10">
                <AlertDialogTitle>
                  {aionStopNextEnabled ? "AION STOP wirklich aktivieren?" : "AION STOP wirklich freigeben?"}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {aionStopNextEnabled
                    ? "AION STOP greift in die vorhandene Safety- und Governor-Laufzeit ein. Die Aktion wird erst nach deiner ausdrücklichen Bestätigung ausgeführt."
                    : "Die Sperre wird erst nach deiner ausdrücklichen Bestätigung aufgehoben. Bestehende Permission- und Safety-Regeln bleiben unverändert aktiv."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="relative z-10">
                <AlertDialogCancel data-testid="trust-center-aion-stop-cancel-button">Abbrechen</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => void runAction("aion-stop", () => setTrustAionStop({ enabled: aionStopNextEnabled, reason: aionStopNextEnabled ? "Trust Center manual stop" : "Trust Center release" }, token))}
                  data-testid="trust-center-aion-stop-confirm-button"
                >
                  {aionStopNextEnabled ? "AION STOP aktivieren" : "AION STOP freigeben"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : null}
        <div className="aion-list" data-testid="trust-center-temporary-permissions-list">
          {(activeTrustCenter.autonomy.temporary_permissions || []).slice(0, 5).map((item, index) => (
            <div className="aion-list-row" key={item.id || index} data-testid={`trust-center-temp-permission-${index}`}>
              <div><strong>{item.capability}</strong><small>{item.resource_type}:{item.resource_id} · bis {item.valid_until}</small></div>
            </div>
          ))}
          {!activeTrustCenter.autonomy.temporary_permissions?.length ? <AionEmptyState title="Keine temporären Freigaben." description="Aktuell läuft nichts zeitlich begrenztes." testId="trust-center-temp-permissions-empty" /> : null}
        </div>
      </AionSectionCard>

      <AionSectionCard title="Private Memory teilen" eyebrow="MEMORY" subtitle="Private Inhalte bleiben privat, bis du sie gezielt freigibst." testId="trust-center-memory-card">
        <div className="aion-settings-grid" data-testid="trust-center-memory-form-grid">
          <label className="aion-settings-field" data-testid="trust-center-share-target-field">
            <span>Freigeben an</span>
            <select value={shareTarget} onChange={(event) => setShareTarget(event.target.value)} data-testid="trust-center-share-target-select">
              <option value="">Person wählen</option>
              {recipients.map((item) => <option key={item.user_id} value={item.user_id}>{item.name}</option>)}
            </select>
          </label>
          <label className="aion-settings-field" data-testid="trust-center-share-reason-field">
            <span>Grund</span>
            <Input value={shareReason} onChange={(event) => setShareReason(event.target.value)} data-testid="trust-center-share-reason-input" />
          </label>
        </div>
        <div className="aion-section-nav" data-testid="trust-center-memory-scopes">
          {scopeOptions.map((scope) => (
            <button
              type="button"
              key={scope.id}
              className={`aion-section-nav-item${selectedScopes.includes(scope.id) ? " aion-section-nav-item-active" : ""}`}
              onClick={() => toggleScope(scope.id)}
              data-testid={`trust-center-scope-${scope.id.toLowerCase()}-button`}
            >
              {scope.label}
            </button>
          ))}
        </div>
        <Button
          onClick={() => runAction("memory-share", async () => {
            await createTrustMemoryGrant({ granted_to_user_id: shareTarget, scopes: selectedScopes, reason: shareReason }, token);
            setShareTarget("");
          })}
          disabled={!shareTarget || !selectedScopes.length}
          data-testid="trust-center-create-share-button"
        >
          {busyKey === "memory-share" ? "Freigabe läuft …" : "Freigabe erstellen"}
        </Button>
        <div className="aion-list" data-testid="trust-center-memory-grants-list">
          {(activeTrustCenter.memory.grants?.outgoing || []).map((grant, index) => (
            <div className="aion-list-row" key={grant.id || index} data-testid={`trust-center-memory-grant-${index}`}>
              <div><strong>{grant.granted_to_user_id}</strong><small>{(grant.scopes || []).join(", ")} · {grant.revoked_at ? "widerrufen" : grant.reason}</small></div>
              {!grant.revoked_at ? (
                <Button variant="outline" onClick={() => runAction(`revoke-grant-${grant.id}`, () => revokeTrustMemoryGrant(grant.id, { reason: "Trust Center revoke" }, token))} data-testid={`trust-center-revoke-grant-${index}-button`}>
                  Widerrufen
                </Button>
              ) : null}
            </div>
          ))}
          {!activeTrustCenter.memory.grants?.outgoing?.length ? <AionEmptyState title="Keine aktiven Freigaben." description="Dein privates Memory bleibt vollständig bei dir." testId="trust-center-memory-grants-empty" /> : null}
        </div>
      </AionSectionCard>

      <AionSectionCard
        title="Remote Access"
        eyebrow="SESSIONS"
        subtitle="Alle aktiven Browser- und Client-Sitzungen auf einen Blick."
        action={sessionSummary.auth.filter((session) => session.status === "ACTIVE" && !session.current).length ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" data-testid="trust-center-revoke-other-auth-sessions-button">
                Andere Browser-Sitzungen beenden
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="aion-confirm-dialog overflow-hidden border-white/70 bg-background/95 shadow-[0_36px_90px_rgba(28,25,23,0.22)] backdrop-blur-xl sm:rounded-[28px]">
              <AlertDialogHeader>
                <AlertDialogTitle>Andere Browser-Sitzungen wirklich beenden?</AlertDialogTitle>
                <AlertDialogDescription>
                  {sessionSummary.auth.filter((session) => session.status === "ACTIVE" && !session.current).length} andere aktive Browser-Sitzung(en) werden widerrufen. Deine aktuelle Sitzung bleibt angemeldet.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => runAction("auth-sessions-revoke-others", () => revokeOtherTrustAuthSessions({ reason: "Trust Center revoke other sessions" }, token))}
                  disabled={busyKey === "auth-sessions-revoke-others"}
                  data-testid="trust-center-revoke-other-auth-sessions-confirm"
                >
                  {busyKey === "auth-sessions-revoke-others" ? "Beendet …" : "Andere Sitzungen beenden"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : null}
        testId="trust-center-sessions-card"
      >
        <div className="aion-list" data-testid="trust-center-auth-sessions-list">
          {sessionSummary.auth.map((session, index) => (
            <div className="aion-list-row" key={session.session_id || index} data-testid={`trust-center-auth-session-${index}`}>
              <div><strong><KeyRound className="aion-list-icon" aria-hidden="true" /> Browser {session.current ? "· aktuell" : ""}</strong><small>{session.client_ip || "Unbekannt"} · {session.status} · {session.last_seen || session.created_at}</small></div>
              {!session.current && session.status === "ACTIVE" ? <Button variant="outline" onClick={() => runAction(`auth-session-${session.session_id}`, () => revokeTrustAuthSession(session.session_id, { reason: "Trust Center revoke" }, token))} data-testid={`trust-center-revoke-auth-session-${index}-button`}>Beenden</Button> : null}
            </div>
          ))}
        </div>
        <div className="aion-list" data-testid="trust-center-client-sessions-list">
          {sessionSummary.clients.map((session, index) => (
            <div className="aion-list-row" key={session.client_session_id || index} data-testid={`trust-center-client-session-${index}`}>
              <div><strong><Smartphone className="aion-list-icon" aria-hidden="true" /> {session.device_id}</strong><small>{session.device_class} · {session.connection_mode} · {session.authentication_state}</small></div>
              {session.authentication_state !== "REVOKED" ? <Button variant="outline" onClick={() => runAction(`client-session-${session.client_session_id}`, () => revokeTrustClientSession(session.client_session_id, { reason: "Trust Center revoke" }, token))} data-testid={`trust-center-revoke-client-session-${index}-button`}>Widerrufen</Button> : null}
            </div>
          ))}
          {!sessionSummary.clients.length ? <AionEmptyState title="Keine AION Client Sessions." description="Aktuell ist kein weiteres AION Gerät angemeldet." testId="trust-center-client-sessions-empty" /> : null}
        </div>
      </AionSectionCard>

      <AionSectionCard title="Security Guardian" eyebrow="GUARDIAN" subtitle="Deduplicated Signale aus Alerts, Sessions und Gerätevertrauen." testId="trust-center-guardian-card">
        <div className="aion-note" data-testid="trust-center-guardian-status">Status: {activeTrustCenter.security_guardian.status}</div>
        <div className="aion-list" data-testid="trust-center-guardian-findings-list">
          {(activeTrustCenter.security_guardian.findings || []).map((finding, index) => (
            <div className="aion-list-row" key={finding.id || index} data-testid={`trust-center-guardian-finding-${index}`}>
              <div><strong><AlertTriangle className="aion-list-icon" aria-hidden="true" /> {finding.title}</strong><small>{finding.summary}</small></div>
              <small>{finding.severity}</small>
            </div>
          ))}
          {!activeTrustCenter.security_guardian.findings?.length ? <AionEmptyState title="Guardian ist ruhig." description="Aktuell gibt es keine aktiven Schutzsignale." testId="trust-center-guardian-empty" /> : null}
        </div>
      </AionSectionCard>

      <AionSectionCard title="Vertrauenswürdige Geräte" eyebrow="GERÄTE" subtitle="Verlorene, widerrufene und vertrauenswürdige Clients bleiben nachvollziehbar." testId="trust-center-devices-card">
        <div className="aion-list" data-testid="trust-center-devices-list">
          {(activeTrustCenter.devices.trusted_clients || []).slice(0, 8).map((device, index) => (
            <div className="aion-list-row" key={device.device_id || index} data-testid={`trust-center-device-${index}`}>
              <div><strong><Shield className="aion-list-icon" aria-hidden="true" /> {device.model || device.device_id}</strong><small>{device.device_class} · {device.trust_status} · {device.lost_mode ? "lost mode" : device.status}</small></div>
            </div>
          ))}
          {!activeTrustCenter.devices.trusted_clients?.length ? <AionEmptyState title="Keine zusätzlichen Geräte." description="Sobald AION Clients eingebunden sind, erscheinen sie hier." testId="trust-center-devices-empty" /> : null}
        </div>
      </AionSectionCard>

      <AionSectionCard title="Audit" eyebrow="PRIVATSPHÄRE & ZUGRIFF" subtitle="Lesbare Zugriffs- und Schutzereignisse aus dem bestehenden Audit-Layer." testId="trust-center-audit-card">
        <div className="aion-list" data-testid="trust-center-audit-list">
          {(activeTrustCenter.audit.entries || []).map((entry, index) => (
            <div className="aion-list-row" key={entry.id || index} data-testid={`trust-center-audit-entry-${index}`}>
              <div><strong><Lock className="aion-list-icon" aria-hidden="true" /> {entry.title}</strong><small>{entry.summary}</small></div>
              <small>{entry.timestamp}</small>
            </div>
          ))}
          {!activeTrustCenter.audit.entries?.length ? <AionEmptyState title="Noch keine Audit-Einträge." description="Neue Privatsphäre- und Sicherheitsereignisse erscheinen hier." testId="trust-center-audit-empty" /> : null}
        </div>
      </AionSectionCard>

      <div className="aion-home-status-inline" data-testid="trust-center-capability-badges">
        <ShieldCheck className="aion-list-icon" aria-hidden="true" />
        <span>Voice-Lokaler Wake-Provider bleibt ehrlich MOCKED bis echte Hardware existiert.</span>
      </div>
    </div>
  );
};